import { editor } from 'monaco-editor';
import { DocumentSymbol, MarkupContent, Position, SymbolInformation } from 'vscode-languageserver-protocol';
import { FrameworkAction, FrameworkActionList, FrameworkArgument, FrameworkClass } from '../../../framework/framework.d';
import { FrameworkClassType } from '../../../framework/framework-types';
import { FrameworkActionLists, FrameworkClasses, UtilityActions } from '../../../framework/frameworkClasses';
import { getInputArgNameForAxisAction } from '../../../framework/framework-axis-actions';
import type { LspClient } from '../../../Components/Editor/Monaco/LspClient';
import { CardComponentList, CardComponents, ICardComponent } from '../Devices/DeviceCardBuilder';
import type { ActionItem, CustomActionItem } from '../../../redux/EditorSlice';
import {
  sequenceTupleLiteralRowsAreEditable,
  sequenceTupleRowsMatchArity,
  tokenizeArgs,
  toJsValue,
} from '../../../Components/Drawers/pythonArgParser';
import { isSequenceTupleArg } from '../../../utils/tupleArg';
import type {
  InputBinding,
  InputBindingAction,
  InputBindingDevice,
  SingleActionInputBinding,
  ToggleInputBinding,
  UnsupportedInputBinding,
} from '../../../utils/inputBindings';
import { createInputBindingId, INPUT_BINDING_DEVICE, INPUT_BINDING_KIND, inputBindingSignature } from '../../../utils/inputBindings';
import { ARG_KEYS, DRIVE_MODE, type DriveMode, MATCH_TIME_DEFAULTS, ROBOT_PROP, RUNMODE } from '../../../utils/constants';
import {
  extractCallArgsText,
  findProgramStartActionCalls,
  parseAutonomousExpression,
  splitTopLevelByComma,
  withHiddenRuntimeActionArgs,
} from './driveModeAutonomousActionUtils';
import {
  findMatchingDelimiterIndex,
  findTopLevelKeywordAssignmentIndex,
  isPlainPythonQuotedStringLiteral,
  readConstructorArgumentValue,
  readPropertyAssignmentValue,
  VAR_ASSIGN_RE,
} from '../../../utils/pythonSourceScanUtils';
import { ROBOT_START_CALL_LINE_RE } from '../../../utils/robotStartCallUtils';
import {
  loadInputBindingDiscoveryFromTreeSitter,
  type InputMappingTriggerCallLike,
  type TreeSitterAxisInputControlCall,
  type TreeSitterInputBindingDiscovery,
  type TreeSitterInputBindingReceiver,
} from '../../../utils/pythonTreeSitterInputBindings';
import { findDriveBaseDefinitions } from '../utils/driveBaseSingleton';
import {
  getMotorVelocityLimitRpmForClassName,
  getMotorVelocityLimitRpmForMotorType,
  parseMotorVelocityDistancePerOutputRotation,
  parseMotorVelocityGearRatio,
  withMotorVelocityLimitForAction,
} from '../../../utils/motorVelocityLimits';

const { CONTROLLER, DRIVEBASE } = INPUT_BINDING_DEVICE;

// LSP SymbolKind: Variable = 13 (top-level mechanism instances are variables)
const SYMBOL_KIND_VARIABLE = 13;

// Shared by the bundled LinearMechanism and RotationalMechanism SDK signatures.
const MECHANISM_CONSTRUCTOR_ARGUMENT_INDEX = {
  MOTOR_TYPE: 1,
  GEAR_RATIO: 5,
  DISTANCE_PER_OUTPUT_ROTATION: 6,
} as const;

// Type guard to check if symbol is SymbolInformation (has location) vs DocumentSymbol (has range)
const isSymbolInformation = (symbol: SymbolInformation | DocumentSymbol): symbol is SymbolInformation => {
  return 'location' in symbol;
};

// Flatten document symbols so we can iterate all symbols (LSP may return hierarchical DocumentSymbol[])
function flattenSymbols(symbols: (SymbolInformation | DocumentSymbol)[]): (SymbolInformation | DocumentSymbol)[] {
  const result: (SymbolInformation | DocumentSymbol)[] = [];
  for (const s of symbols) {
    result.push(s);
    if ('children' in s && s.children?.length) {
      result.push(...flattenSymbols(s.children));
    }
  }
  return result;
}

// Class names in FrameworkClasses that are Mechanism or Electronic (drive bases, mechanisms) - used for UI panels
function getMechanismAndBitClassNames(): Set<string> {
  const set = new Set<string>();
  for (const fc of FrameworkClasses) {
    if (fc.type === FrameworkClassType.Mechanism || fc.type === FrameworkClassType.Electronic) {
      set.add(fc.name);
    }
  }
  return set;
}

const MECHANISM_AND_BIT_CLASS_NAMES = getMechanismAndBitClassNames();
const ALL_FRAMEWORK_CLASS_NAMES = new Set<string>(FrameworkClasses.map((c) => c.name));
const FRAMEWORK_ACTION_GROUP_CLASS_NAMES = new Set(
  FrameworkActionLists.flatMap((list) => list.actions || [])
    .filter((action) => action?.type === 'group' && action.name)
    .map((action) => action.name)
);

const UTILITY_ACTION_NAMES = new Set<string>((UtilityActions.actions || []).map((a) => String(a?.name || '').trim()).filter(Boolean));
const UTILITY_ACTION_BY_NAME = new Map<string, FrameworkAction>(
  (UtilityActions.actions ?? [])
    .filter((action): action is FrameworkAction & { name: string } => Boolean(action?.name))
    .map((action) => [action.name, action])
);

/** Parent label for a utility action in action lists and parsed bindings. */
export function utilityActionParentName(action: FrameworkAction): string {
  return action.standalone ? String(action.name) : 'UtilityActions';
}

function findUtilityActionItem(actionList: ActionItem[], actionName: string): ActionItem | undefined {
  const frameworkAction = UTILITY_ACTION_BY_NAME.get(actionName);
  if (!frameworkAction) return undefined;
  const parentName = utilityActionParentName(frameworkAction);
  return actionList.find(
    (item) => (item.parentName || '') === parentName && (item.mechanismName || undefined) === undefined && item.action?.name === actionName
  );
}

function resolveUtilityActionBindingContext(
  actionName: string,
  actionList: ActionItem[]
): { parentName: string; defArgs: FrameworkArgument[] } | null {
  const frameworkAction = UTILITY_ACTION_BY_NAME.get(actionName);
  if (!frameworkAction) return null;
  const parentName = utilityActionParentName(frameworkAction);
  const actionItem = findUtilityActionItem(actionList, actionName);
  return {
    parentName,
    defArgs: actionItem?.action?.arguments ?? frameworkAction.arguments ?? [],
  };
}

function isNonSingletonDriveBaseAssignment(className: string | undefined, lineIndex: number, firstDriveBaseLine: number | undefined): boolean {
  return className === 'DriveBase' && lineIndex !== firstDriveBaseLine;
}

function isIndentedCodeLine(lines: string[], lineIndex: number): boolean {
  return /^\s/.test(lines[lineIndex] ?? '');
}

/** True when `item` is a static utility action (always available; not tied to a hardware variable). */
export function isUtilityFrameworkActionItem(item: { parentName?: string; parentClass?: FrameworkClass; mechanismClass?: FrameworkClass }): boolean {
  return (
    String(item.parentName || '') === 'UtilityActions' ||
    item.mechanismClass?.type === FrameworkClassType.Utility ||
    item.parentClass?.type === FrameworkClassType.Utility
  );
}

/** True when `item` is always available regardless of declared hardware variables. */
export function isAlwaysAvailableFrameworkActionItem(item: {
  parentName?: string;
  parentClass?: FrameworkClass;
  mechanismClass?: FrameworkClass;
  action?: FrameworkAction;
}): boolean {
  if (item.action?.standalone === true) return true;
  if (isUtilityFrameworkActionItem(item)) return true;

  const parentName = String(item.parentName || '').trim();
  const actionName = String(item.action?.name || '').trim();
  if (parentName && parentName === actionName && UTILITY_ACTION_BY_NAME.get(actionName)?.standalone) {
    return true;
  }

  return false;
}

/** True when the action may only be used inside action groups (not standalone bindings). */
export function isGroupOnlyFrameworkAction(action: FrameworkAction | null | undefined): boolean {
  if (action?.groupOnly === true) return true;
  const actionName = String(action?.name || '').trim();
  return actionName.length > 0 && UTILITY_ACTION_BY_NAME.get(actionName)?.groupOnly === true;
}

/** Removes group-only actions from standalone binding pickers (controller inputs, program start, etc.). */
export function excludeGroupOnlyActions(actionList: ActionItem[]): ActionItem[] {
  return actionList.filter((item) => !isGroupOnlyFrameworkAction(item.action));
}

/** @deprecated Use {@link excludeGroupOnlyActions} */
export const filterActionsForInputBindings = excludeGroupOnlyActions;

function appendUtilityActionItems(actionList: ActionItem[]): void {
  UtilityActions.actions?.forEach((action) => {
    if (!action?.name) return;
    const parentName = utilityActionParentName(action);
    actionList.push({
      parentName,
      parentClass: UtilityActions,
      mechanismName: undefined,
      mechanismClass: UtilityActions,
      action,
    });
  });
}

/** Move all utility `ActionItem`s to the end (after mechanisms / motors / etc.). */
function reorderActionListWithUtilitiesLast(actionList: ActionItem[]): void {
  const utilityItems = actionList.filter(isUtilityFrameworkActionItem);
  if (utilityItems.length === 0) return;
  const rest = actionList.filter((it) => !isUtilityFrameworkActionItem(it));
  actionList.length = 0;
  actionList.push(...rest, ...utilityItems);
}

function parseArgumentTokensFromCall(argsStr: string, defArgs: FrameworkArgument[]): FrameworkArgument[] {
  if (!argsStr.trim()) return [];
  const tokens = tokenizeArgs(argsStr, { ignoreLineComments: true });
  const defArgByName = new Map(defArgs.map((arg) => [arg.name, arg]));
  const isQuotedPythonLiteral = (rawValue: string) => isPlainPythonQuotedStringLiteral(rawValue.trim());
  const buildArg = (name: string, rawValue: string, def?: FrameworkArgument): FrameworkArgument => {
    const trimmed = rawValue.trim();
    const { type, value } = toJsValue(trimmed);
    if (def && isSequenceTupleArg(def)) {
      // Only hydrate when every element is an arity-matching tuple of scalar literals; anything else
      // (`get_lines()`, `[foo()]`, `[(1, "A", extra)]`, `[(1, message_var)]`, ...) is preserved verbatim
      // as unsupported code so saving can't rewrite or truncate user-authored Python (e.g. turn a
      // variable lookup into a quoted string literal).
      if (sequenceTupleLiteralRowsAreEditable(trimmed, def.members ?? [])) {
        return { name, type: def.type, members: def.members, value };
      }
      return {
        name,
        type: def.type,
        members: def.members,
        value: trimmed,
        rawSourceExpression: isQuotedPythonLiteral(trimmed) ? undefined : trimmed,
      };
    }
    if (def && String(def.type || '').toLowerCase() === 'tuple') {
      const members = def.members || [];
      if (!Array.isArray(value)) {
        return {
          name,
          type: 'tuple',
          value,
          members,
          rawSourceExpression: isQuotedPythonLiteral(trimmed) ? undefined : trimmed,
        };
      }
      const valueByName: Record<string, unknown> = {};
      members.forEach((member, memberIndex) => {
        valueByName[member.name] = value[memberIndex] ?? member.defaultValue;
      });
      return {
        name,
        type: 'tuple',
        value: valueByName,
        members,
      };
    }
    return {
      name,
      type: def?.type || type,
      value,
      rawSourceExpression: trimmed,
    };
  };
  return tokens.map((tok, idx) => {
    const eqIdx = findTopLevelKeywordAssignmentIndex(tok);
    if (eqIdx > 0) {
      const name = tok.slice(0, eqIdx).trim();
      return buildArg(name, tok.slice(eqIdx + 1), defArgByName.get(name));
    }
    const def = defArgs[idx];
    const name = def?.name || `arg${idx + 1}`;
    return buildArg(name, tok, def);
  });
}

// ─── Shared parsing primitives ────────────────────────────────────────────────
// Centralised here so every scanning function uses the same patterns and helpers.

/** Mark devices without code evidence as represented so stale Redux bindings clear on re-parse. */
function markAbsentInputBindingDevicesForStaleClear(devices: Set<InputBinding['device']>): void {
  if (!devices.has(CONTROLLER)) devices.add(CONTROLLER);
  if (!devices.has(DRIVEBASE)) devices.add(DRIVEBASE);
}

/** Devices represented in code by Tree-sitter receivers and/or parsed bindings. */
function getInputBindingDevicesRepresentedInCode(
  receivers: ReadonlyArray<Pick<TreeSitterInputBindingReceiver, 'device'>>,
  parsedInputBindingDevices: ReadonlySet<InputBinding['device']> = new Set()
): Set<InputBinding['device']> {
  const devices = new Set<InputBinding['device']>();

  for (const receiver of receivers) {
    devices.add(receiver.device);
  }

  for (const device of parsedInputBindingDevices) {
    devices.add(device);
  }

  markAbsentInputBindingDevicesForStaleClear(devices);

  return devices;
}

/** Extracts `parent[.mechanism].action(` from a mapping callback expression */
const ACTION_TARGET_RE = /^\s*([a-zA-Z_][\w]*)\.(?:([a-zA-Z_][\w]*)\.)?([a-zA-Z_][\w]*)\s*\(/;

/** Detects an explicit null/None un-mapping */
const NULL_MAPPING_RE = /^\s*(None|null)\s*$/i;

/** Detects a bare identifier used as a custom action reference */
const SIMPLE_IDENT_RE = /^([A-Za-z_][A-Za-z0-9_]*)\s*$/;
const UNSUPPORTED_CONTROLLER_BINDING_BUTTONS = new Set(['l3', 'r3', 'left_stick', 'right_stick', 'leftstick', 'rightstick']);

export { VAR_ASSIGN_RE };

/** Extracts a class name from LSP hover markup: `class ClassName` */
const LSP_CLASS_NAME_RE = /class\s+([A-Za-z0-9_]+)/i;

/** Matches a Python function definition line: `def fnName(params):` */
const PYTHON_DEF_RE = /^\s*def\s+([a-zA-Z_][\w]*)\s*\(([^)]*)\)\s*:/;

/** Matches the start of any Python function definition (used as a scan sentinel) */
const PYTHON_DEF_START_RE = /^\s*def\s+/;

/** Escapes a string for safe interpolation into a RegExp */
function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const ACTION_GROUP_CLASS_NAME_PATTERN = Array.from(FRAMEWORK_ACTION_GROUP_CLASS_NAMES).map(escapeRegex).join('|') || '(?!)';

/** Detects a framework action-group constructor usage (optionally prefixed with `return` or a qualifier) */
const ACTION_GROUP_CONSTRUCTOR_RE = new RegExp(String.raw`(?:return\s+)?(?:\w+\.)?(?:${ACTION_GROUP_CLASS_NAME_PATTERN})\s*\(`);

/** Matches `varName = [qualifier.]<FrameworkActionGroup>(` assignment */
const ACTION_GROUP_ASSIGN_RE = new RegExp(String.raw`^\s*([a-zA-Z_][\w]*)\s*=\s*(?:\w+\.)?(?:${ACTION_GROUP_CLASS_NAME_PATTERN})\s*\(`);

/**
 * Custom action names from code (framework action-group assignments and
 * functions that return a framework action group). Used so bare identifiers /
 * calls that collide with `UtilityActions` names resolve to Custom, not utility.
 */
function collectFunctionDefinedActionGroups(lines: string[], symbols: (SymbolInformation | DocumentSymbol)[]): CustomActionItem[] {
  const customActions: CustomActionItem[] = [];
  const functionSymbols = (symbols || []).filter((s) => s?.kind === 12); // 12 = Function
  for (const fn of functionSymbols) {
    const startLine: number | undefined = isSymbolInformation(fn) ? fn.location?.range?.start?.line : fn.range?.start?.line;
    if (startLine == null) continue;
    const defLine = lines[startLine] ?? '';
    const defMatch = defLine.match(PYTHON_DEF_RE);
    if (!defMatch) continue;
    const fnName = defMatch[1];

    const windowSize = 80;
    let hasGroupReturn = false;
    for (let i = startLine + 1; i <= Math.min(lines.length - 1, startLine + windowSize); i++) {
      const ln = lines[i];
      if (PYTHON_DEF_START_RE.test(ln)) break;
      if (ACTION_GROUP_CONSTRUCTOR_RE.test(ln)) {
        hasGroupReturn = true;
        break;
      }
    }
    if (hasGroupReturn && fnName) customActions.push({ name: fnName, lineNumber: startLine });
  }
  return customActions;
}

function isValidUiPanel(panel: string | null): panel is CardComponentList {
  return panel !== null && Object.keys(CardComponents).includes(panel);
}

function isSupportedInputBindingButton(device: InputBindingDevice, button: string): boolean {
  if (device !== CONTROLLER) return true;
  return !UNSUPPORTED_CONTROLLER_BINDING_BUTTONS.has(String(button || '').toLowerCase());
}

export type ProgramStartActionConfig = {
  selection: string;
  argValues: Record<string, string>;
  rawExpression?: string;
};

export type StartCallConfig = {
  driveMode: DriveMode;
  autonomousTime: string;
  driveControlTime: string;
  autoSelection: string;
  autoArgValues: Record<string, string>;
  autonomousActionExpression?: string;
  rawAutonomousActionCode?: string;
  programStartActions: ProgramStartActionConfig[];
};

function extractStartCallArgsText(lines: string[], startLineIdx: number): string | null {
  const line = lines[startLineIdx] ?? '';
  const dotStartIdx = line.search(/\.\s*start\s*\(/);
  if (dotStartIdx < 0) return null;
  const openOffset = line.slice(dotStartIdx).indexOf('(');
  if (openOffset < 0) return null;
  return extractCallArgsText(lines, startLineIdx, dotStartIdx + openOffset)?.argsText ?? null;
}

function parseProgramStartActionsFromLines(lines: string[]): ProgramStartActionConfig[] {
  const programStartActions: ProgramStartActionConfig[] = [];
  findProgramStartActionCalls(lines).forEach((call) => {
    const parsed = parseAutonomousExpression(call.argsText.trim());
    const rawExpression = parsed.selection ? undefined : parsed.rawExpression;
    if (parsed.selection || rawExpression) {
      programStartActions.push({
        selection: parsed.selection,
        argValues: parsed.argValues,
        rawExpression,
      });
    }
  });
  return programStartActions;
}

/**
 * Parses the extracted args text of a `start(...)` call into a structured config.
 */
function parseStartArgsText(argsText: string, programStartActions: ProgramStartActionConfig[] = []): StartCallConfig {
  const rawArgs = argsText.trim().length ? splitTopLevelByComma(argsText).map((s) => s.trim()) : [];
  let modeToken: string | undefined;
  let autoTime: string | undefined;
  let driverTime: string | undefined;
  let autonomousAction: string | undefined;

  const getRhs = (s: string) => s.split('=').slice(1).join('=').trim();

  rawArgs.forEach((arg, idx) => {
    if (/=/.test(arg)) {
      if (new RegExp(`^${ARG_KEYS.MODE}\\s*=`).test(arg)) modeToken = getRhs(arg);
      else if (new RegExp(`^${ARG_KEYS.AUTO_MATCH_TIME}\\s*=`).test(arg)) autoTime = getRhs(arg);
      else if (new RegExp(`^${ARG_KEYS.DRIVER_MATCH_TIME}\\s*=`).test(arg)) driverTime = getRhs(arg);
      else if (new RegExp(`^${ROBOT_PROP.AUTONOMOUS_ACTION}\\s*=`).test(arg)) autonomousAction = getRhs(arg);
    } else {
      if (idx === 0 && !modeToken) modeToken = arg;
    }
  });

  const token = (modeToken || '').trim();
  let driveMode: DriveMode = DRIVE_MODE.DRIVE_CONTROL;
  if (token === RUNMODE.AUTONOMOUS) driveMode = DRIVE_MODE.AUTONOMOUS;
  else if (token === RUNMODE.MATCH) driveMode = DRIVE_MODE.MATCH;

  const {
    selection: autoSelection,
    argValues: autoArgValues,
    rawExpression: rawAutonomousActionCode,
  } = autonomousAction ? parseAutonomousExpression(autonomousAction.trim()) : { selection: '', argValues: {} };

  return {
    driveMode,
    autonomousTime: autoTime ?? MATCH_TIME_DEFAULTS.AUTO,
    driveControlTime: driverTime ?? MATCH_TIME_DEFAULTS.DRIVER,
    autoSelection,
    autoArgValues,
    autonomousActionExpression: autonomousAction?.trim(),
    rawAutonomousActionCode,
    programStartActions,
  };
}

/**
 * Scans code text for the first `robot.start(...)` call and parses its arguments
 * into a structured config used to populate the Drive Mode UI.
 * Exported for standalone use (e.g. tests). The main parsing pipeline gathers
 * program start actions once, then calls `extractStartCallArgsText` +
 * `parseStartArgsText` inside the existing lightweight line scan.
 */
export function parseStartCallFromCode(code: string): StartCallConfig | null {
  if (!code) return null;
  const lines = code.split(/\r?\n/);
  const programStartActions = parseProgramStartActionsFromLines(lines);
  for (let i = 0; i < lines.length; i++) {
    if (!ROBOT_START_CALL_LINE_RE.test(lines[i] ?? '')) continue;
    const argsText = extractStartCallArgsText(lines, i);
    if (argsText !== null) return parseStartArgsText(argsText, programStartActions);
    break;
  }
  return null;
}

/**
 * Lightweight hardware card list from program text (no LSP).
 * Used when deriving drive-unit port occupancy for offline export/duplicate paths.
 */
export function buildHardwareCardsFromProjectCodeSync(code: string): ICardComponent[] {
  if (!code.trim()) return [];

  const actionList: ActionItem[] = [];
  const axisActionList: ActionItem[] = [];
  const lines = code.split(/\r?\n/);
  const firstDriveBaseLine = findDriveBaseDefinitions(code)[0]?.lineIndex;
  const handledVars = new Set<string>();
  const newCards: ICardComponent[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (isIndentedCodeLine(lines, i)) continue;

    const m = line.match(VAR_ASSIGN_RE);
    if (!m) continue;
    const varName = m[1] ?? '';
    const className = m[2] ?? '';
    if (!varName || !className) continue;
    if (!MECHANISM_AND_BIT_CLASS_NAMES.has(className)) continue;
    if (isNonSingletonDriveBaseAssignment(className, i, firstDriveBaseLine)) continue;

    handledVars.add(varName);
    const cards = checkForUiPanel({
      className,
      parentName: varName,
      lineNumber: i,
      actionList,
      axisActionList,
      code,
    });
    newCards.push(...cards);
  }

  return newCards;
}

export type HardwareParseResult = {
  dynamicCards: ICardComponent[];
  actionList: ActionItem[];
  axisActionList: ActionItem[];
  inputBindingList: InputBinding[];
  customActionList: CustomActionItem[];
  frameworkActionLists: FrameworkActionList[];
  startCallConfig: StartCallConfig | null;
  /**
   * False when Tree-sitter binding discovery or hydrate failed and existing Redux bindings
   * were preserved. Callers must not treat that run as a successful Control hydrate.
   */
  inputBindingDiscoverySucceeded: boolean;
};

export async function parseHardwareFromProjectCode(args: {
  code: string;
  editor?: editor.IStandaloneCodeEditor;
  lspClient?: LspClient;
  existingInputBindings: InputBinding[];
  logger: { error: (message: string, err?: unknown) => void };
}): Promise<HardwareParseResult> {
  const { code, existingInputBindings, logger } = args;
  const actionList: ActionItem[] = [];
  appendUtilityActionItems(actionList);
  const axisActionList: ActionItem[] = [];
  const inputBindingList: InputBinding[] = [];
  const customActionList: CustomActionItem[] = [];

  // Prevent duplicate mappings during the same parse pass
  const bindingSignatures = new Set<string>();
  const addBindingIfUnique = (binding: InputBinding) => {
    const normalized = {
      ...binding,
      id: createInputBindingId(binding),
    } as InputBinding;
    const sig = inputBindingSignature(normalized);
    if (!bindingSignatures.has(sig)) {
      bindingSignatures.add(sig);
      inputBindingList.push(normalized);
    }
  };

  let startCallConfig: StartCallConfig | null = null;

  // Tree-sitter owns input-binding discovery (no scanner fallback). Fail soft on
  // WASM/init/parse errors so cards, actions, and robot.start still populate.
  // When discovery fails, keep existing Redux bindings (do not treat empty IR as
  // "all devices represented" and wipe Control state).
  let discovery: TreeSitterInputBindingDiscovery;
  let inputBindingDiscoveryFailed = false;
  try {
    discovery = await loadInputBindingDiscoveryFromTreeSitter(code);
  } catch (err) {
    logger.error('Tree-sitter input binding parse failed', err);
    inputBindingDiscoveryFailed = true;
    discovery = {
      receivers: [],
      triggersByReceiver: new Map(),
      axisCallsByController: new Map(),
    };
  }
  const triggersByReceiver = discovery.triggersByReceiver;
  const axisCallsByController = discovery.axisCallsByController;
  const treeSitterReceivers = discovery.receivers;

  const hydrateBindingsOrMarkFailed = (
    axisActionList: ActionItem[],
    addBindingIfUnique: (binding: InputBinding) => void,
    actionList: ActionItem[],
    customActionNames: ReadonlySet<string>,
    hydrateLogger: { error: (message: string, err?: unknown) => void }
  ) => {
    const hydrated = hydrateInputBindingsFromTreeSitter(
      axisActionList,
      addBindingIfUnique,
      actionList,
      customActionNames,
      triggersByReceiver,
      treeSitterReceivers,
      axisCallsByController,
      hydrateLogger
    );
    if (!hydrated) {
      // Same preserve policy as Tree-sitter discovery failure: do not stale-clear from a partial list.
      inputBindingDiscoveryFailed = true;
      inputBindingList.length = 0;
    }
  };

  const buildCardList = async (
    buildArgs: {
      code: string;
      actionList: ActionItem[];
      axisActionList: ActionItem[];
      customActionList: CustomActionItem[];
      addBindingIfUnique: (binding: InputBinding) => void;
    },
    deps: {
      editor?: editor.IStandaloneCodeEditor;
      lspClient?: LspClient;
      logger: { error: (message: string, err?: unknown) => void };
    }
  ): Promise<ICardComponent[]> => {
    const { code, actionList, axisActionList, customActionList, addBindingIfUnique } = buildArgs;
    const { lspClient, editor, logger } = deps;
    const newCards: ICardComponent[] = [];

    const mechanismAndBitClassNames = MECHANISM_AND_BIT_CLASS_NAMES;

    // Lightweight single-pass scan (no LSP required): populates hardware cards and drive-mode
    // config from plain text. Runs first so the UI is responsive even before LSP is ready.
    const lines = code.split(/\r?\n/);
    const firstDriveBaseLine = findDriveBaseDefinitions(code)[0]?.lineIndex;
    const programStartActions = parseProgramStartActionsFromLines(lines);
    const handledVars = new Set<string>();
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i] ?? '';

      // Detect robot.start(...) and extract drive-mode config in the same pass.
      if (startCallConfig === null && ROBOT_START_CALL_LINE_RE.test(line)) {
        const argsText = extractStartCallArgsText(lines, i);
        if (argsText !== null) startCallConfig = parseStartArgsText(argsText, programStartActions);
        continue;
      }

      if (isIndentedCodeLine(lines, i)) continue;

      const m = line.match(VAR_ASSIGN_RE);
      if (!m) continue;
      const varName = m[1] ?? '';
      const className = m[2] ?? '';
      if (!varName || !className) continue;
      if (!mechanismAndBitClassNames.has(className)) continue;
      if (isNonSingletonDriveBaseAssignment(className, i, firstDriveBaseLine)) continue;

      handledVars.add(varName);
      const cards = checkForUiPanel({
        className,
        parentName: varName,
        lineNumber: i,
        actionList,
        axisActionList,
        editor,
        code,
      });
      newCards.push(...cards);
    }

    const canUseLsp =
      !!lspClient &&
      typeof (lspClient as unknown as { getDocumentSymbolInfo?: unknown }).getDocumentSymbolInfo === 'function' &&
      typeof (lspClient as unknown as { getHoverInfo?: unknown }).getHoverInfo === 'function';

    if (!canUseLsp) {
      discoverCustomActions(code, [], customActionList, logger);
      const customActionNameSet = new Set(customActionList.map((c) => c.name));
      hydrateBindingsOrMarkFailed(axisActionList, addBindingIfUnique, actionList, customActionNameSet, logger);
      return newCards;
    }

    // Baseline text scans should always run, regardless of whether LSP is ready.
    // LSP-based symbol/hover enrichment is optional and must not suppress parsing.
    const controllerDecls: Array<{ varName: string; className: 'ActionController' | 'ActionDriveBasePanel' }> = treeSitterReceivers.map(
      (receiver) => ({ varName: receiver.name, className: receiver.className })
    );

    let symbolsForCustomActions: (SymbolInformation | DocumentSymbol)[] = [];
    try {
      const withTimeout = async <T>(p: Promise<T>, ms: number, label: string): Promise<T> => {
        let t: ReturnType<typeof setTimeout> | undefined;
        try {
          return await Promise.race([
            p,
            new Promise<T>((_, reject) => {
              t = setTimeout(() => reject(new Error(`Timeout: ${label}`)), ms);
            }),
          ]);
        } finally {
          if (t) clearTimeout(t);
        }
      };

      // Ensure the LSP server has the latest text before reading symbols.
      try {
        await withTimeout(lspClient.updateTextDocument(code), 250, 'updateTextDocument');
      } catch {
        // Best-effort: if sync is slow (cold LSP) or fails, still proceed with whatever the server has.
      }

      const symbolsResult = await withTimeout(lspClient.getDocumentSymbolInfo(), 1500, 'getDocumentSymbolInfo');
      const symbols = flattenSymbols((symbolsResult || []) as (SymbolInformation | DocumentSymbol)[]);
      symbolsForCustomActions = symbols;

      // Enrich action pickers with ActionController / ActionDriveBasePanel framework actions.
      // Binding hydration itself happens once in hydrateInputBindingsFromTreeSitter from Tree-sitter IR.
      for (const decl of controllerDecls) {
        appendInputPanelFrameworkActions(decl.varName, decl.className, actionList);
      }

      // Identify top-level variables that are instances of known framework classes.
      const candidateSymbols = symbols.filter((symbol) => {
        if (symbol.kind !== SYMBOL_KIND_VARIABLE) return false;
        if (handledVars.has(symbol.name)) return false;
        return true;
      });
      candidateSymbols.sort((a, b) => {
        const aLine = isSymbolInformation(a) ? a.location.range.start.line : a.range.start.line;
        const bLine = isSymbolInformation(b) ? b.location.range.start.line : b.range.start.line;
        return aLine - bLine;
      });

      // LSP hover can be slow; avoid fully serial awaits.
      const CONCURRENCY = 6;
      let firstLspDriveBaseLine: number | undefined;
      for (let i = 0; i < candidateSymbols.length; i += CONCURRENCY) {
        const chunk = candidateSymbols.slice(i, i + CONCURRENCY);
        const results = await Promise.all(
          chunk.map(async (symbol) => {
            const lineNumber = isSymbolInformation(symbol) ? symbol.location.range.start.line : symbol.range.start.line;
            try {
              const hoverInfo = await withTimeout(
                lspClient.getHoverInfo(code, { line: lineNumber, character: 0 } satisfies Position),
                1500,
                `getHoverInfo:${symbol.name}`
              );
              const hoverClass = parseClassName(hoverInfo?.contents as MarkupContent);
              return { symbol, lineNumber, hoverClass, hoverError: null as unknown };
            } catch (err) {
              return { symbol, lineNumber, hoverClass: null as string | null, hoverError: err as unknown };
            }
          })
        );

        for (const r of results) {
          const { symbol, lineNumber, hoverClass } = r;
          if (!hoverClass) continue;
          if (!mechanismAndBitClassNames.has(hoverClass)) continue;
          if (hoverClass === 'DriveBase') {
            if (firstDriveBaseLine !== undefined) {
              if (lineNumber !== firstDriveBaseLine) continue;
            } else {
              if (isIndentedCodeLine(lines, lineNumber)) continue;
              firstLspDriveBaseLine ??= lineNumber;
              if (lineNumber !== firstLspDriveBaseLine) continue;
            }
          }

          const cards = checkForUiPanel({
            className: hoverClass,
            parentName: symbol.name,
            lineNumber,
            actionList,
            axisActionList,
            editor,
            code,
          });
          newCards.push(...cards);

          if (/ActionController/i.test(hoverClass) || /ActionDriveBasePanel/i.test(hoverClass)) {
            appendInputPanelFrameworkActions(symbol.name, hoverClass, actionList);
          }
        }
      }
    } catch (err) {
      logger.error('Error parsing mechanisms', err);
    } finally {
      // Ensure baseline parsing always runs, even if LSP symbols/hover time out or throw.
      // LSP enrichment should not block controller mappings, custom actions, or axis mappings.
      discoverCustomActions(code, symbolsForCustomActions, customActionList, logger);
      const customActionNameSet = new Set(customActionList.map((c) => c.name));
      hydrateBindingsOrMarkFailed(axisActionList, addBindingIfUnique, actionList, customActionNameSet, logger);
    }

    return newCards;
  };

  const dynamicCards = await buildCardList(
    { code, actionList, axisActionList, customActionList, addBindingIfUnique },
    { editor: args.editor, lspClient: args.lspClient, logger }
  );

  const existing = existingInputBindings || [];
  // Replace bindings for devices represented in code (even when the parse finds zero mappings),
  // so deleting mapping code or managed input regions clears stale Redux/UI state.
  // If Tree-sitter discovery or hydrate failed, preserve existing bindings instead of stale-clearing.
  const scannedDevices = inputBindingDiscoveryFailed
    ? new Set<InputBinding['device']>()
    : getInputBindingDevicesRepresentedInCode(treeSitterReceivers, new Set(inputBindingList.map((binding) => binding.device)));
  const hasCurrentCode = code.trim().length > 0;
  const hasNoInputBindingRepresentation = scannedDevices.size === 0 && inputBindingList.length === 0 && dynamicCards.length === 0;
  const mergedInputBindingList = inputBindingDiscoveryFailed
    ? existing
    : hasCurrentCode && !hasNoInputBindingRepresentation
    ? [...existing.filter((it: InputBinding) => !scannedDevices.has(it.device)), ...inputBindingList]
    : inputBindingList;

  reorderActionListWithUtilitiesLast(actionList);

  return {
    dynamicCards,
    actionList,
    axisActionList,
    inputBindingList: mergedInputBindingList,
    customActionList,
    frameworkActionLists: FrameworkActionLists || [],
    startCallConfig,
    inputBindingDiscoverySucceeded: !inputBindingDiscoveryFailed,
  };
}

function parseClassName(hoverContents: MarkupContent): string | null {
  if (!hoverContents) return null;
  const value = (hoverContents as MarkupContent)?.value || '';
  if (!value) return null;
  const match = value.match(LSP_CLASS_NAME_RE);
  if (!match) return null;
  return match[1] || null;
}

const triggerTokenToBindingKind = (triggerToken: string): SingleActionInputBinding['kind'] =>
  triggerToken === 'on_released'
    ? INPUT_BINDING_KIND.RELEASED
    : triggerToken === 'on_change'
    ? INPUT_BINDING_KIND.VARIABLE
    : INPUT_BINDING_KIND.PRESSED;

const buildInputBindingAction = (
  parentName: string,
  mechanismName: string | undefined,
  actionName: string,
  args: FrameworkArgument[] = [],
  parentClass?: FrameworkClass,
  mechanismClass?: FrameworkClass
): InputBindingAction => ({
  parentName,
  parentClass,
  mechanismName,
  mechanismClass,
  action: {
    name: actionName,
    arguments: args,
  },
});

const buildSingleActionBinding = (
  device: InputBindingDevice,
  button: string,
  kind: SingleActionInputBinding['kind'],
  action: InputBindingAction,
  sourceLineNumber?: number,
  receiverVarName?: string
): SingleActionInputBinding => {
  const binding = {
    id: '',
    device,
    button,
    kind,
    action,
    receiverVarName,
    sourceLineNumber,
  };
  return {
    ...binding,
    id: createInputBindingId(binding),
  };
};

const buildToggleBinding = (
  device: InputBindingDevice,
  button: string,
  onAction: InputBindingAction,
  offAction: InputBindingAction,
  sourceLineNumber?: number,
  receiverVarName?: string
): ToggleInputBinding => {
  const binding = {
    id: '',
    device,
    button,
    kind: INPUT_BINDING_KIND.TOGGLE,
    onAction,
    offAction,
    receiverVarName,
    sourceLineNumber,
  };
  return {
    ...binding,
    id: createInputBindingId(binding),
  };
};

const buildUnsupportedInputBindingAction = (rawActionExpression: string): InputBindingAction => {
  const rawExpression = rawActionExpression.trim();
  return {
    ...buildInputBindingAction('Custom', undefined, rawExpression || 'Unsupported mapping', []),
    unsupportedRawExpression: rawExpression,
  };
};

const buildUnsupportedInputBinding = (
  device: InputBindingDevice,
  button: string,
  trigger: UnsupportedInputBinding['trigger'],
  rawCode: string,
  rawActionExpression: string,
  sourceLineNumber?: number,
  receiverVarName?: string
): UnsupportedInputBinding => {
  const binding = {
    id: '',
    device,
    button,
    kind: INPUT_BINDING_KIND.UNSUPPORTED,
    trigger,
    rawCode: rawCode.trim(),
    rawActionExpression: rawActionExpression.trim(),
    receiverVarName,
    sourceLineNumber,
    action: buildUnsupportedInputBindingAction(rawActionExpression),
  };
  return {
    ...binding,
    id: createInputBindingId(binding),
  };
};

const buildToggleOrUnsupportedInputBindingFromArgs = (
  device: InputBindingDevice,
  button: string,
  rawCode: string,
  rawActionExpression: string,
  sourceLineNumber: number | undefined,
  actionList: ActionItem[],
  customActionNames: ReadonlySet<string>,
  receiverVarName?: string
): InputBinding | null => {
  const toggleArgs = tokenizeArgs(rawActionExpression);
  if (toggleArgs.length !== 2) {
    return buildUnsupportedInputBinding(device, button, INPUT_BINDING_KIND.TOGGLE, rawCode, rawActionExpression, sourceLineNumber, receiverVarName);
  }
  if (toggleArgs.some((arg) => NULL_MAPPING_RE.test(arg))) return null;

  const onAction =
    parseInputBindingActionExpression(toggleArgs[0], actionList, customActionNames) ?? buildUnsupportedInputBindingAction(toggleArgs[0]);
  const offAction =
    parseInputBindingActionExpression(toggleArgs[1], actionList, customActionNames) ?? buildUnsupportedInputBindingAction(toggleArgs[1]);
  return buildToggleBinding(device, button, onAction, offAction, sourceLineNumber, receiverVarName);
};

/**
 * Formatted nested calls inside `on_pressed` / `on_released` often end with a
 * trailing comma after the action's closing `)` (Python style). That comma is
 * not part of the action expression and must not mark the binding unsupported.
 */
function isInsignificantTrailingCallSuffix(suffix: string): boolean {
  const leftover = suffix.trim();
  return leftover.length === 0 || leftover === ',';
}

function parseInputBindingActionExpression(
  actionExpr: string,
  actionList: ActionItem[] = [],
  customActionNames?: ReadonlySet<string>
): InputBindingAction | null {
  if (NULL_MAPPING_RE.test(actionExpr)) return null;

  const trimmed = actionExpr.trim();

  const targetMatch = trimmed.match(ACTION_TARGET_RE);
  if (targetMatch) {
    let parentName = targetMatch[1] ?? '';
    const mechanismName = targetMatch[2] ?? undefined;
    const actionName = targetMatch[3] ?? '';

    let parsedArgs: FrameworkArgument[] = [];
    const openIdx = trimmed.indexOf('(');
    const closeIdx = openIdx >= 0 ? findMatchingDelimiterIndex(trimmed, openIdx, '(', ')') : -1;
    if (openIdx < 0 || closeIdx <= openIdx || !isInsignificantTrailingCallSuffix(trimmed.slice(closeIdx + 1))) return null;

    const argsStr = trimmed.substring(openIdx + 1, closeIdx).trim();
    if (argsStr.length > 0) {
      const utilityContext = UTILITY_ACTION_NAMES.has(actionName) ? resolveUtilityActionBindingContext(actionName, actionList) : null;
      if (utilityContext) {
        parentName = utilityContext.parentName;
        parsedArgs = parseArgumentTokensFromCall(argsStr, utilityContext.defArgs);
      } else {
        const actionDef = actionList.find(
          (it) =>
            (it.parentName || '') === parentName && (it.mechanismName || undefined) === (mechanismName || undefined) && it.action?.name === actionName
        );
        const defArgs = withHiddenRuntimeActionArgs(actionDef?.action)?.arguments || [];
        parsedArgs = parseArgumentTokensFromCall(argsStr, defArgs);
      }
    } else if (UTILITY_ACTION_NAMES.has(actionName)) {
      parentName = resolveUtilityActionBindingContext(actionName, actionList)?.parentName ?? parentName;
    }

    const utilityMeta = parentName === 'UtilityActions' || UTILITY_ACTION_BY_NAME.get(parentName)?.standalone ? UtilityActions : undefined;
    return buildInputBindingAction(parentName, mechanismName, actionName, parsedArgs, utilityMeta, utilityMeta);
  }

  const unqual = trimmed.match(/^\s*([A-Za-z_][\w]*)\s*\(/);
  if (unqual) {
    const actionName = unqual[1] ?? '';
    const openIdx = trimmed.indexOf('(');
    const closeIdx = findMatchingDelimiterIndex(trimmed, openIdx, '(', ')');
    if (openIdx >= 0 && closeIdx > openIdx && isInsignificantTrailingCallSuffix(trimmed.slice(closeIdx + 1))) {
      const argsStr = trimmed.substring(openIdx + 1, closeIdx).trim();
      if (customActionNames?.has(actionName)) {
        const parsedArgs = parseArgumentTokensFromCall(argsStr, []);
        return buildInputBindingAction('Custom', undefined, actionName, parsedArgs);
      }
      if (UTILITY_ACTION_NAMES.has(actionName)) {
        const utilityContext = resolveUtilityActionBindingContext(actionName, actionList);
        if (!utilityContext) return null;
        const parsedArgs = parseArgumentTokensFromCall(argsStr, utilityContext.defArgs);
        return buildInputBindingAction(utilityContext.parentName, undefined, actionName, parsedArgs, UtilityActions, UtilityActions);
      }
    }
  }

  const simpleIdentifier = trimmed.match(SIMPLE_IDENT_RE);
  if (simpleIdentifier) {
    const ident = simpleIdentifier[1] ?? '';
    if (customActionNames?.has(ident)) {
      return buildInputBindingAction('Custom', undefined, ident, []);
    }
    if (UTILITY_ACTION_NAMES.has(ident)) {
      const utilityContext = resolveUtilityActionBindingContext(ident, actionList);
      if (!utilityContext) return null;
      return buildInputBindingAction(utilityContext.parentName, undefined, ident, [], UtilityActions, UtilityActions);
    }
    return buildInputBindingAction('Custom', undefined, ident, []);
  }

  return null;
}

interface MotorVelocityLimitContext {
  limitRpm: number;
  gearRatio?: number | null;
  distancePerOutputRotation?: number | null;
}

function getMotorVelocityLimitContextForClassInstance(
  foundClass: FrameworkClass,
  code: string | undefined,
  lineNumber: number,
  parentName: string
): MotorVelocityLimitContext | null {
  const directLimit = getMotorVelocityLimitRpmForClassName(foundClass.name);
  if (directLimit !== null) return { limitRpm: directLimit };

  const motorTypeDefault = foundClass.initialization.find((init) => init.parameter === 'motor_type')?.value;
  if (!motorTypeDefault) return null;

  const propertyMotorType = readPropertyAssignmentValue(code, parentName, 'motor_type', lineNumber);
  const declaredMotorType = readConstructorArgumentValue(code, lineNumber, 'motor_type', MECHANISM_CONSTRUCTOR_ARGUMENT_INDEX.MOTOR_TYPE);
  if (declaredMotorType === null && propertyMotorType === undefined) return null;
  const limitRpm = getMotorVelocityLimitRpmForMotorType(propertyMotorType ?? declaredMotorType ?? String(motorTypeDefault));
  if (limitRpm === null) return null;

  const gearRatioDefault = foundClass.initialization.find((init) => init.parameter === 'gear_ratio')?.value;
  const propertyGearRatio = readPropertyAssignmentValue(code, parentName, 'gear_ratio', lineNumber);
  const declaredGearRatio = readConstructorArgumentValue(code, lineNumber, 'gear_ratio', MECHANISM_CONSTRUCTOR_ARGUMENT_INDEX.GEAR_RATIO);
  if (declaredGearRatio === null && propertyGearRatio === undefined) return { limitRpm, gearRatio: null };
  const rawGearRatio = propertyGearRatio ?? declaredGearRatio ?? gearRatioDefault ?? 1;
  const gearRatio = parseMotorVelocityGearRatio(rawGearRatio);
  if (gearRatio === null) return { limitRpm, gearRatio: null };

  const distancePerOutputRotationDefault = foundClass.initialization.find((init) => init.parameter === 'distance_per_output_rotation')?.value;
  if (distancePerOutputRotationDefault === undefined) return { limitRpm, gearRatio };

  const propertyDistancePerOutputRotation = readPropertyAssignmentValue(code, parentName, 'distance_per_output_rotation', lineNumber);
  const declaredDistancePerOutputRotation = readConstructorArgumentValue(
    code,
    lineNumber,
    'distance_per_output_rotation',
    MECHANISM_CONSTRUCTOR_ARGUMENT_INDEX.DISTANCE_PER_OUTPUT_ROTATION
  );
  if (declaredDistancePerOutputRotation === null && propertyDistancePerOutputRotation === undefined) {
    return { limitRpm, gearRatio, distancePerOutputRotation: null };
  }
  const rawDistancePerOutputRotation = propertyDistancePerOutputRotation ?? declaredDistancePerOutputRotation ?? distancePerOutputRotationDefault;
  const distancePerOutputRotation = parseMotorVelocityDistancePerOutputRotation(rawDistancePerOutputRotation);
  if (distancePerOutputRotation === null) return { limitRpm, gearRatio, distancePerOutputRotation: null };

  return { limitRpm, gearRatio, distancePerOutputRotation };
}

type CheckForUiPanelArgs = {
  className: string;
  parentName: string;
  lineNumber: number;
  actionList: ActionItem[];
  axisActionList: ActionItem[];
  editor?: editor.IStandaloneCodeEditor;
  code?: string;
};

function checkForUiPanel({ className, parentName, lineNumber, actionList, axisActionList, editor, code }: CheckForUiPanelArgs): ICardComponent[] {
  const cards: ICardComponent[] = [];
  const foundClass = FrameworkClasses.find((x) => x.name === className);
  if (!foundClass) return cards;

  const uiPanelType = foundClass.uiPanel || null;
  if (isValidUiPanel(uiPanelType)) {
    const cardHeader = `${parentName} (${foundClass.name})`;
    const newCard: ICardComponent = {
      type: uiPanelType,
      props: {
        id: `${uiPanelType.toLowerCase()}-${parentName}`,
        header: cardHeader,
        editor,
        initialOpen: false,
        lineNumber,
        parentName,
        parentClass: undefined,
        mechanismName: parentName,
        mechanismClass: foundClass,
        parameterName: '',
      },
    };
    cards.push(newCard);
  }

  // Populate action lists from framework metadata
  const motorVelocityLimitContext = getMotorVelocityLimitContextForClassInstance(foundClass, code, lineNumber, parentName);
  if (foundClass.actions && foundClass.actions.length > 0) {
    foundClass.actions.forEach((action) => {
      const actionWithLimits = withMotorVelocityLimitForAction(action, motorVelocityLimitContext?.limitRpm, {
        gearRatio: motorVelocityLimitContext?.gearRatio,
        distancePerOutputRotation: motorVelocityLimitContext?.distancePerOutputRotation,
      });
      actionList.push({
        parentName,
        mechanismClass: foundClass,
        action: actionWithLimits,
      });
    });
  }
  if (foundClass.axisActions && foundClass.axisActions.length > 0) {
    foundClass.axisActions.forEach((action) => {
      const actionWithLimits = withMotorVelocityLimitForAction(action as unknown as ActionItem['action'], motorVelocityLimitContext?.limitRpm, {
        gearRatio: motorVelocityLimitContext?.gearRatio,
        distancePerOutputRotation: motorVelocityLimitContext?.distancePerOutputRotation,
      });
      axisActionList.push({
        parentName,
        mechanismClass: foundClass,
        action: actionWithLimits,
      });
    });
  }

  return cards;
}

/**
 * Adds framework actions for an ActionController / ActionDriveBasePanel instance to
 * the shared action list so pickers can offer them. Binding discovery/hydration is
 * owned by `hydrateInputBindingsFromTreeSitter` from Tree-sitter IR (not duplicated here).
 */
function appendInputPanelFrameworkActions(varName: string, className: string, actionList: ActionItem[]): void {
  const frameworkClass = FrameworkClasses.find((x) => x.name === className);
  if (!frameworkClass?.actions) return;
  for (const action of frameworkClass.actions) {
    if (!actionList.some((it) => it.action?.name === action?.name)) {
      actionList.push({
        parentName: varName,
        parentClass: undefined,
        mechanismName: undefined,
        mechanismClass: frameworkClass,
        action,
      });
    }
  }
}

function discoverCustomActions(
  code: string,
  symbols: (SymbolInformation | DocumentSymbol)[],
  customActionList: CustomActionItem[],
  logger: { error: (message: string, err?: unknown) => void }
): void {
  try {
    const lines = code.split(/\r?\n/);
    const foundNames = new Set<string>();

    const addCustom = (name: string, lineNumber?: number) => {
      if (!name || foundNames.has(name)) return;
      foundNames.add(name);
      customActionList.push({ name, lineNumber });
    };

    collectFunctionDefinedActionGroups(lines, symbols).forEach(({ name, lineNumber }) => addCustom(name, lineNumber));

    for (let i = 0; i < lines.length; i++) {
      const m = lines[i].match(ACTION_GROUP_ASSIGN_RE);
      if (m) addCustom(m[1], i);
    }
  } catch (err) {
    logger.error('Error discovering custom actions', err);
  }
}

function getterToShoulderButton(getter: string): string | null {
  switch (getter) {
    case 'get_r1_axis':
      return 'r1';
    case 'get_l1_axis':
      return 'l1';
    case 'get_r2_axis':
      return 'r2';
    case 'get_l2_axis':
      return 'l2';
    default:
      return null;
  }
}

/**
 * Builds UI `InputBinding`s from Tree-sitter discovery IR (receivers, button
 * triggers, and axis/variable controls). Does not re-scan source text for bindings.
 * Returns false when hydration throws so callers can preserve existing Redux bindings.
 */
function hydrateInputBindingsFromTreeSitter(
  axisActionList: ActionItem[],
  addBindingIfUnique: (binding: InputBinding) => void,
  actionList: ActionItem[],
  customActionNames: ReadonlySet<string>,
  triggersByReceiver: ReadonlyMap<string, InputMappingTriggerCallLike[]>,
  treeSitterReceivers: TreeSitterInputBindingReceiver[],
  axisCallsByController: ReadonlyMap<string, TreeSitterAxisInputControlCall[]>,
  logger: { error: (message: string, err?: unknown) => void }
): boolean {
  try {
    const vars: { name: string; device: InputBindingDevice }[] = treeSitterReceivers.map((receiver) => ({
      name: receiver.name,
      device: receiver.device,
    }));
    if (vars.length === 0) return true;

    const controllerVars = vars.filter((v) => v.device === CONTROLLER).map((v) => v.name);
    for (const controllerVarName of controllerVars) {
      for (const axisCall of axisCallsByController.get(controllerVarName) ?? []) {
        const actionPathParts = axisCall.actionPath.split('.').filter(Boolean);
        const parentName = actionPathParts[0] ?? '';
        const mechanismName = actionPathParts.length > 1 ? actionPathParts.slice(1).join('.') : undefined;
        const matchedActionName = axisCall.actionName;
        const button = getterToShoulderButton(axisCall.getterName);
        if (!button || !matchedActionName) continue;

        // Nested / same-line wrappers are delete-only unsupported rows (whole-line edits would drop surrounding structure).
        if (axisCall.isWrapped) {
          addBindingIfUnique(
            buildUnsupportedInputBinding(
              CONTROLLER,
              button,
              INPUT_BINDING_KIND.VARIABLE,
              axisCall.rawCode,
              `${axisCall.actionPath}.${matchedActionName}`,
              axisCall.sourceLineNumber,
              controllerVarName
            )
          );
          continue;
        }

        const actionDef = axisActionList.find(
          (it) =>
            (it.parentName || '') === parentName &&
            (it.mechanismName || undefined) === (mechanismName || undefined) &&
            it.action?.name === matchedActionName
        );

        let parsedArgs: { name: string; type: string; value: unknown }[] = [];
        if (axisCall.argsTokens.length > 0) {
          const inputArgName = getInputArgNameForAxisAction(matchedActionName);
          const defArgs = (actionDef?.action?.arguments || []).filter((a) => a.name !== inputArgName);
          const defArgByName = new Map<string, FrameworkArgument>(defArgs.map((a) => [a.name, a as FrameworkArgument]));
          const finalizeArg = (name: string, type: string, value: unknown, defArg: FrameworkArgument | undefined, rawValue?: string) => {
            if (defArg && isSequenceTupleArg(defArg)) {
              // Hydrate only arity-matching rows of scalar literals; rows with identifiers/expressions
              // (e.g. `[(1, message_var)]`) are preserved verbatim so saving can't quote a variable.
              const rawSeq = String(rawValue ?? '').trim();
              const seqMembers = defArg.members ?? [];
              const editable = rawSeq
                ? sequenceTupleLiteralRowsAreEditable(rawSeq, seqMembers)
                : sequenceTupleRowsMatchArity(value, seqMembers.length);
              if (editable) {
                return { name, type: defArg.type, members: defArg.members, value };
              }
              return { name, type: defArg.type, members: defArg.members, value: rawSeq, rawSourceExpression: rawSeq || undefined };
            }
            if (defArg && String(defArg.type || '').toLowerCase() === 'tuple') {
              const members = defArg.members || [];
              if (!Array.isArray(value)) {
                return { name, type: 'tuple', value, members, rawSourceExpression: String(rawValue ?? value).trim() };
              }
              const arr = Array.isArray(value) ? value : [];
              const valueByName: Record<string, unknown> = {};
              members.forEach((member, mIdx) => {
                valueByName[member.name] = arr[mIdx] ?? member.defaultValue;
              });
              // Keep members so delete/replace can rebuild formatted color tuples and match source.
              return { name, type: 'tuple', value: valueByName, members };
            }
            return { name, type, value };
          };
          const tokens = axisCall.argsTokens;
          parsedArgs = tokens.map((tok, idx) => {
            const eqIdx = findTopLevelKeywordAssignmentIndex(tok);
            if (eqIdx > 0) {
              const name = tok.slice(0, eqIdx).trim();
              const rawValue = tok.slice(eqIdx + 1);
              const { type, value } = toJsValue(rawValue);
              return finalizeArg(name, type, value, defArgByName.get(name), rawValue);
            }
            const def = defArgs[idx];
            const name = def?.name || `arg${idx + 1}`;
            const { type, value } = toJsValue(tok);
            return finalizeArg(name, type, value, def as FrameworkArgument | undefined, tok);
          });
        }

        addBindingIfUnique(
          buildSingleActionBinding(
            CONTROLLER,
            button,
            INPUT_BINDING_KIND.VARIABLE,
            buildInputBindingAction(
              parentName,
              mechanismName,
              matchedActionName,
              parsedArgs as unknown as FrameworkArgument[],
              actionDef?.parentClass,
              actionDef?.mechanismClass
            ),
            axisCall.sourceLineNumber,
            controllerVarName
          )
        );
      }
    }

    for (const v of vars) {
      for (const triggerCall of triggersByReceiver.get(v.name) ?? []) {
        const { buttonName, triggerToken, rawActionExpression, rawCode, sourceLineNumber, isWrapped } = triggerCall;
        const buttonAccessor = buttonName;
        if (!isSupportedInputBindingButton(v.device, buttonAccessor)) continue;

        // Same-line wrappers (`if`, `return`, assignment, …) are delete-only unsupported rows.
        if (isWrapped) {
          const wrappedKind = triggerToken === 'on_toggle' ? INPUT_BINDING_KIND.TOGGLE : triggerTokenToBindingKind(triggerToken);
          addBindingIfUnique(
            buildUnsupportedInputBinding(v.device, buttonAccessor, wrappedKind, rawCode, rawActionExpression, sourceLineNumber, v.name)
          );
          continue;
        }

        if (triggerToken === 'on_toggle') {
          const binding = buildToggleOrUnsupportedInputBindingFromArgs(
            v.device,
            buttonAccessor,
            rawCode,
            rawActionExpression,
            sourceLineNumber,
            actionList,
            customActionNames,
            v.name
          );
          if (binding) addBindingIfUnique(binding);
          continue;
        }

        const kind = triggerTokenToBindingKind(triggerToken);
        if (NULL_MAPPING_RE.test(rawActionExpression)) continue;
        const action = parseInputBindingActionExpression(rawActionExpression, actionList, customActionNames);
        if (!action) {
          addBindingIfUnique(buildUnsupportedInputBinding(v.device, buttonAccessor, kind, rawCode, rawActionExpression, sourceLineNumber, v.name));
          continue;
        }
        addBindingIfUnique(buildSingleActionBinding(v.device, buttonAccessor, kind, action, sourceLineNumber, v.name));
      }
    }
    return true;
  } catch (err) {
    logger.error('Tree-sitter input binding hydrate failed', err);
    return false;
  }
}

// ─── Exported text-scan utilities ────────────────────────────────────────────
// These are used by useAllAvailableActions so that all code scanning lives here.

/**
 * Lightweight text-only scan: derives ActionItems for all mechanism instances
 * declared in `code`, without needing LSP or async parsing.
 *
 * Used as a fast fallback/staleness guard in action pickers. Internally
 * delegates to `checkForUiPanel` (the same function the full parser uses) so
 * the two code paths stay in sync automatically.
 */
export function deriveActionsFromCode(code: string): ActionItem[] {
  const actionList: ActionItem[] = [];

  if (code?.trim()) {
    const dummyAxisList: ActionItem[] = [];

    const lines = code.split(/\r?\n/);
    const firstDriveBaseLine = findDriveBaseDefinitions(code)[0]?.lineIndex;
    const seen = new Set<string>();

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i] ?? '';
      if (isIndentedCodeLine(lines, i)) continue;

      const m = line.match(VAR_ASSIGN_RE);
      if (!m) continue;
      const varName = m[1] ?? '';
      const className = m[2] ?? '';
      if (!varName || !className || !ALL_FRAMEWORK_CLASS_NAMES.has(className)) continue;
      if (isNonSingletonDriveBaseAssignment(className, i, firstDriveBaseLine)) continue;
      if (seen.has(varName)) continue;
      seen.add(varName);

      checkForUiPanel({
        className,
        parentName: varName,
        lineNumber: i,
        actionList,
        axisActionList: dummyAxisList,
        code,
      });
    }
  }

  appendUtilityActionItems(actionList);
  return actionList;
}

/**
 * Scans `code` for top-level variable assignment declarations
 * (`foo = ClassName(...)`) and returns the set of declared variable names.
 *
 * Used by action pickers to decide whether Redux-cached parsed results still
 * match the currently loaded code (stale-state guard).
 */
export function collectDeclaredParentNames(code: string): Set<string> {
  const names = new Set<string>();
  const lines = code.split(/\r?\n/);
  const firstDriveBaseLine = findDriveBaseDefinitions(code)[0]?.lineIndex;
  for (let i = 0; i < lines.length; i++) {
    if (isIndentedCodeLine(lines, i)) continue;

    const m = lines[i]?.match(VAR_ASSIGN_RE);
    if (!m) continue;
    if (isNonSingletonDriveBaseAssignment(m[2], i, firstDriveBaseLine)) continue;
    if (m[1]) names.add(m[1]);
  }
  return names;
}
